// Recently viewed stocks, kept on the user's account (GET/POST /v1/recents) so the Market page and
// the search box offer them on every device. One module-level list, shared by every component and
// kept between pages; a view moves the stock to the front at once and is saved in the background.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

/** A recently viewed stock, with its price and the day's change when the feed has them. */
export interface Recent { symbol: string; price?: number; changePct?: number }

let list: Recent[] | null = null;
let loading: Promise<void> | null = null;
const subs = new Set<(l: Recent[]) => void>();
const emit = () => { for (const f of subs) f(list ?? []); };

function load() {
  loading ??= api('/v1/recents')
    .then((r) => (r.ok ? r.json() : []))
    .then((vs: Recent[]) => { list = vs.map(({ symbol, price, changePct }) => ({ symbol, price, changePct })); emit(); })
    .catch(() => {})
    .finally(() => { loading = null; });
  return loading;
}

/** Fetch the list again (fresh prices), e.g. when the Market page opens. */
export const refreshRecents = () => void load();

/** The user's recently viewed stocks, newest first. */
export function useRecents(): Recent[] {
  const [l, setL] = useState<Recent[]>(list ?? []);
  useEffect(() => {
    subs.add(setL);
    if (list) setL(list); else void load();
    return () => { subs.delete(setL); };
  }, []);
  return l;
}

/** Record a stock page view. */
export function recordView(symbol: string) {
  const was = (list ?? []).find((r) => r.symbol === symbol);
  list = [was ?? { symbol }, ...(list ?? []).filter((r) => r.symbol !== symbol)].slice(0, 20);
  emit();
  void api('/v1/recents', { method: 'POST', body: JSON.stringify({ symbol }) }).catch(() => {});
}

/** Remove a stock from the list. */
export function forgetView(symbol: string) {
  list = (list ?? []).filter((r) => r.symbol !== symbol);
  emit();
  void api(`/v1/recents/${encodeURIComponent(symbol)}`, { method: 'DELETE' }).catch(() => {});
}
